import { languageDisplayLabel, LanguagesEnum } from '@/api/enums';
import { handleAxiosError } from '@/api/helpers';
import { client } from '@/client';
import {
  Course,
  CourseAttempt,
  CourseContent,
  CourseContentType,
  CourseStatusAttemptEnum,
  ResourceType,
} from '@/client/courses';
import { RedirectPaths, RedirectPathsEnum } from '@/common/constants';
import { AppContext } from '@/common/context/AppContext';
import { CourseLanguageSelectScreen } from '@/components/courses/CourseLanguageSelectScreen';
import { useCourseAttempt } from '@/hooks/query';
import { useAppDispatch, useAppSelector } from '@/hooks/store';
import { useToast } from '@/hooks/useToast';
import { ScormProvider } from '@/scorm/context/ScormContext';
import { selectCurrentUser, setCurrentUser } from '@/store/features/users';
import { AppButton } from '@/ui/buttons';
import { FlexContainer } from '@/ui/styled-ui';

import { LearnerCourseDispatchResponse } from '@/client/learner-course';
import { QuizType } from '@/client/quizzes';
import { ContinueOrFinishButton } from '@/components/courses/ContinueOrFinishButton';
import { VideoFallbackMessage } from '@/components/courses/VideoFallbackMessage';
import { Quiz as QuizComponent } from '@/components/quiz';
import { QuizFeedback } from '@/components/quiz/QuizFeedback';
import { SlideTextImageTemplateViewer } from '@/components/slides/learner/slide-templates/SlideTextImageTemplateViewer';
import { DoughnutChart } from '@/ui/charts/doughnut-chart';
import ContentPDFViewer from '@/ui/pdf-viewer/ContentPDFViewer';
import { getCurrentVideoFile, isRtlLanguage } from '@/utils/helpers';
import { headingLargeFontSize } from '@/utils/helpers/css-variables.helper';
import { isMobile } from '@/utils/helpers/ui.helper';
import { AxiosError } from 'axios';
import { ProgressSpinner } from 'primereact/progressspinner';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { AccessibilityIcon } from '../components/AccessibilityIcon';
import { ContentActionHeader } from '../components/ContentActionHeader';
import OrientationWarning from '../components/OrientationWarning';
import { ScormVersion } from '../types';
import { attemptLog } from '../utils/attempt-log.helper';
import { determineSelectedLanguage } from '../utils/content';

const StyledFlexContainer = styled(FlexContainer)`
  word-break: break-word;
  min-height: 100%;

  &.stretch {
    height: 100%;
  }

  .min-score-text {
    margin: 0;
    font-size: var(--default-font-size);
  }

  @media screen and (max-width: 424px) {
    &.stretch {
      height: auto;
    }
  }
`;

const StyledWrapper = styled(FlexContainer)<{ isMobile: boolean }>`
  width: 100%;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: var(--beige-main);
  border-radius: var(--default-border-radius);
  border: 1px solid var(--gray-dark);
  position: relative;
  flex: 1;

  ${({ isMobile }) =>
    isMobile &&
    css`
      height: auto;
      margin-top: var(--small-padding);
      margin-bottom: var(--small-padding);
    `}
`;

const StyledButton = styled(AppButton)`
  width: 28px !important;
  height: 28px !important;
  padding: 0 !important;

  &.minimize-button,
  &.exit-button,
  &.next-button {
    position: absolute;
    right: var(--default-padding);
    top: var(--medium-padding);
    z-index: 999;
  }

  &.next-button {
    right: calc(var(--default-padding) * 3);
  }

  &.exit-button {
    top: var(--xsmall-padding);
    right: var(--xxsmall-padding);
  }

  &.minimize-button {
    background: var(--purple-middle);
    border-color: var(--purple-middle);
  }
`;

const StyledNextButton = styled(AppButton)`
  position: absolute;
  right: -48px;
  top: 50%;
`;

const StyledSpinnerDiv = styled.div`
  margin: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  background-color: var(--white-main);
  padding: var(--default-padding);
  border-radius: var(--default-border-radius);
  opacity: 0.8;
  z-index: 9;
`;

const StyledContentContainer = styled.div`
  position: relative;
  display: flex;
  margin: auto;
  width: 70%;
  max-width: 1000px;

  &.mobile {
    position: static;
  }

  &::before {
    content: '';
    width: 0;
    padding-bottom: 56.25%; /* 16:9 aspect ratio */
    display: block;
  }

  @media screen and (max-width: 767px) {
    & {
      width: 100%;
    }
  }
`;

const StyledIframe = styled.iframe`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  border: none;
`;

const StyledVideo = styled.video`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  border: none;
`;

const customCoursePropertyValueMap: Record<string, string | boolean> = {
  True: true,
  False: false,
};

const hasScormContent = (modules: CourseContent[]): boolean => {
  return modules.some(
    (module) =>
      module.type === CourseContentType.RESOURCE &&
      module.resource?.type === ResourceType.SCORM,
  );
};

export const ContentPlayer: React.FC<{ course: Course }> = ({ course }) => {
  const { t } = useTranslation();
  const { triggerIsFullScreen } = useContext(AppContext);
  const navigate = useNavigate();
  const toast = useToast();
  const dispatch = useAppDispatch();
  const { id: courseId, attemptId } = useParams();
  const currentUser = useAppSelector(selectCurrentUser);
  const [status, setStatus] = useState<CourseStatusAttemptEnum>();
  const [score, setScore] = useState<number>();
  const [isContentStarted, setIsContentStarted] = useState<boolean>(false);
  const [isUrlLoading, setIsUrlLoading] = useState<boolean>(false);
  const [isUserLoading, setIsUserLoading] = useState<boolean>(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [frameLoaded, setFrameLoaded] = useState(false);
  const [isSubmittingStatus, setIsSubmittingStatus] = useState<boolean>(false);
  const [isBellowThreshold, setIsBellowThreshold] = useState<boolean>(false);
  const [isCurrentModuleCompleted, setIsCurrentModuleCompleted] =
    useState<boolean>(false);
  const docStyle = getComputedStyle(document.documentElement);

  const [modules, setModules] = useState<CourseContent[]>([]);
  const [currentModule, setCurrentModule] = useState<number>(0);
  const [currentModuleContent, setCurrentModuleContent] =
    useState<CourseContent | null>(null);
  const [hasConfirmedRead, setHasConfirmedRead] = useState<boolean>(false);
  const [scormDetails, setScormDetails] =
    useState<LearnerCourseDispatchResponse | null>(null);

  const [attemptLanguage, setAttemptLanguage] = useState<LanguagesEnum>();

  const abortControllerRef = useRef<AbortController | null>(null);
  const isRTL = isRtlLanguage(attemptLanguage);

  useEffect(() => {
    if (!triggerIsFullScreen) return;

    if (isMobile()) {
      triggerIsFullScreen(true);
    }

    return () => {
      triggerIsFullScreen(false);
    };
  }, []);

  useEffect(() => {
    const contentElement = document.querySelector('.content');

    if (contentElement) {
      contentElement.classList.add('content-viewer-active');

      return () => {
        contentElement.classList.remove('content-viewer-active');
      };
    }
  }, []);

  useEffect(() => {
    // Refetch user data so it is certain that changes like minimum score are up to date
    setIsUserLoading(true);
    const fetchUser = async () => {
      const user = await client.default.getMe(currentUser?.account.id);
      dispatch(setCurrentUser(user));
    };
    fetchUser();
    setIsUserLoading(false);
  }, []);

  useEffect(() => {
    if (!currentUser?.firstName || !frameLoaded || !isContentStarted) return;

    let interval: any = null;

    const setUserData = () => {
      if (
        typeof (iframeRef.current?.contentWindow as any)?.GetPlayer ===
        'undefined'
      ) {
        return;
      }

      const player = (iframeRef.current?.contentWindow as any)?.GetPlayer();

      if (player) {
        player.SetVar('Firstname', currentUser?.firstName);
        player.SetVar('ITperson', currentUser?.email);
        player.SetVar('useremail', currentUser?.email);
        player.SetVar('usermanager', currentUser?.companyData?.manager ?? '');

        if (
          modules?.length &&
          scormDetails?.properties &&
          Object.keys(scormDetails?.properties || {}).length > 0
        ) {
          Object.entries(scormDetails?.properties || {})
            .filter(([, v]) => !!scormDetails?.properties && !!v)
            .forEach(([k, v]) => {
              let value: string | boolean = v;

              if (customCoursePropertyValueMap[v]) {
                value = customCoursePropertyValueMap[v];
              }

              player.SetVar(k, value);
            });
        }

        if (interval) clearInterval(interval);
      }
    };

    interval = setInterval(setUserData, 200);
  }, [
    frameLoaded,
    currentUser?.firstName,
    currentUser?.email,
    isContentStarted,
    scormDetails?.properties,
  ]);

  const {
    courseAttempt: scormCourseAttempt,
    isLoading: isAttemptLoading,
    refetch: refetchAttempt,
  } = useCourseAttempt({
    courseId,
    attemptId,
  });

  const [selectedLanguage, setSelectedLanguage] = useState<
    LanguagesEnum | undefined
  >(undefined);

  useEffect(() => {
    if (!currentUser || !course || isAttemptLoading) {
      return;
    }

    // Set initial preselected language if not available set to English
    setSelectedLanguage(determineSelectedLanguage(currentUser, course));

    // Workaround for wrong attemptId given in reminder emails
    const checkAttemptExistance = async () => {
      if (course?.id && attemptId) {
        const attempt = await client.learnerCourses.updateCourseAttempt({
          courseId: course.id,
          attemptId: attemptId,
          language: determineSelectedLanguage(currentUser, course),
        });

        if (attempt.id !== attemptId) {
          navigate(
            RedirectPaths[RedirectPathsEnum.COURSE_PLAY](course.id, attempt.id),
            { replace: true },
          );
        }
      }
    };

    checkAttemptExistance();
  }, [currentUser, course, scormCourseAttempt]);

  useEffect(() => {
    const handleCourseStart = async () => {
      if (course?.selfHosted && course?.courseLanguages?.length === 1) {
        setIsContentStarted(true);
      }

      if (currentUser?.contentLanguage && !isMobile()) {
        setIsContentStarted(true);

        await attemptLog(
          course,
          attemptId,
          `Course started - course: ${course.id}, user language: ${currentUser?.contentLanguage}`,
        );
      }
    };

    handleCourseStart();
    fetchData();
  }, [course, scormCourseAttempt]);

  useEffect(() => {
    setStatus(scormCourseAttempt?.status);
    setScore(scormCourseAttempt?.score);
  }, [scormCourseAttempt]);

  useEffect(() => {
    fetchData();
    setCurrentModule(0);
  }, [selectedLanguage]);

  useEffect(() => {
    if (attemptId !== scormCourseAttempt?.id) {
      refetchAttempt();
    }
  }, [attemptId]);

  useEffect(() => {
    // Reset completion state when module changes
    setIsCurrentModuleCompleted(false);
    setHasConfirmedRead(false);
  }, [currentModule]);

  const updateCourseAttemptLanguage = async (language: LanguagesEnum) => {
    try {
      if (attemptId) {
        await client.learnerCourses.updateCourseAttempt({
          courseId: course.id,
          attemptId: attemptId,
          language,
          updateContentLanguage: true,
        });
      }
    } catch (error) {
      handleAxiosError(error as Error | AxiosError, toast);
    }
  };

  const getLanguage = () => {
    if (course?.selfHosted && course?.courseLanguages?.length === 1) {
      return course?.courseLanguages[0];
    }

    if (course && !course?.selfHosted) {
      return LanguagesEnum.EN;
    }

    return selectedLanguage || LanguagesEnum.EN;
  };

  const fetchData = async () => {
    const courseLanguage = getLanguage();
    setAttemptLanguage(courseLanguage);

    if (course && courseLanguage) {
      const modules = course.courseContents?.sort(
        (a, b) => a.position - b.position,
      );

      setModules(modules || []);
    }
  };

  useEffect(() => {
    const fetchCurrentModule = async () => {
      if (!course?.id || !modules?.[currentModule]?.id) return;

      try {
        const module = modules[currentModule];

        if (module.type === CourseContentType.RESOURCE) {
          const resourceData = await client.courses.getCourseResource(
            course.id,
            module.resource?.id || '',
          );

          if (resourceData) {
            setCurrentModuleContent({
              ...module,
              resource: resourceData,
            });
          }
        } else if (module.type === CourseContentType.QUIZ) {
          const quizData = await client.courses.getCourseQuiz(
            course.id,
            module.quiz?.id || '',
          );

          if (quizData) {
            setCurrentModuleContent({
              ...module,
              quiz: quizData,
            });
          }
        }
      } catch (e) {
        handleAxiosError(e as Error | AxiosError, toast);
      }
    };

    fetchCurrentModule();
  }, [currentModule, course?.id, modules]);

  useEffect(() => {
    // Abort previous request if it exists
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const fetchScormDetails = async () => {
      if (!course?.id || !currentModuleContent?.resource?.id) return;

      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      if (abortController.signal.aborted) {
        return;
      }

      const language = getLanguage();

      try {
        setIsUrlLoading(true);
        const scormDetails = await client.courses.getResourceDispatchUrl(
          course?.id,
          currentModuleContent.resource.id,
          language,
          abortController.signal,
        );

        if (!abortController.signal.aborted) {
          setScormDetails(scormDetails);
        }

        await attemptLog(
          course,
          attemptId,
          `Course file fetched - course: ${course.id}, language: ${language}, file: ${scormDetails.url}`,
        );
      } catch (e) {
        if (!abortController.signal.aborted) {
          handleAxiosError(e as Error | AxiosError, toast);
        }
      } finally {
        if (!abortController.signal.aborted) {
          setIsUrlLoading(false);
        }

        if (abortControllerRef.current === abortController) {
          abortControllerRef.current = null;
        }
      }
    };

    if (currentModuleContent?.resource?.type === ResourceType.SCORM) {
      fetchScormDetails();
    }

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
  }, [currentModuleContent, modules, attemptLanguage, course?.id]);

  const handleQuizCompletion = async (currentScore?: number) => {
    if (!course?.id || !attemptId || !attemptLanguage) return;

    await client.learnerCourses.updateCourseAttempt({
      courseId: course.id,
      attemptId: attemptId,
      language: attemptLanguage,
      payload: {
        status: CourseStatusAttemptEnum.COMPLETED,
        score: currentScore || score || 0,
      },
    });

    setStatus(CourseStatusAttemptEnum.COMPLETED);
    toast?.success(t('toast.success'), t('content.completed.success'));
  };

  const onQuizFinish = async () => {
    if (currentModule !== modules.length - 1) {
      setIsCurrentModuleCompleted(true);
      setCurrentModule(currentModule + 1);
    } else {
      navigate(RedirectPaths[RedirectPathsEnum.LEARNER_DASHBOARD_ACTIVE]());
    }
  };

  const handleModuleCompletion = async (
    currentScore?: number,
    isLastModuleCompleteble?: boolean,
  ) => {
    if (!course?.id || !attemptId || !attemptLanguage) return;

    try {
      // Do not update course attempt if there is SCORM content ( it's handled in ScormContext)
      if (
        currentModuleContent?.quiz?.type === QuizType.TRADITIONAL &&
        !hasScormContent(modules)
      ) {
        await handleQuizCompletion(currentScore);
      } else if (
        isLastModuleCompleteble &&
        !hasScormContent(modules) &&
        !modules.some((module) => module.quiz?.type === QuizType.TRADITIONAL)
      ) {
        // Handle completion when there are no traditional quizzes and no SCORM content
        await client.learnerCourses.updateCourseAttempt({
          courseId: course.id,
          attemptId: attemptId,
          language: attemptLanguage,
          payload: {
            status: CourseStatusAttemptEnum.COMPLETED,
          },
        });

        setStatus(CourseStatusAttemptEnum.COMPLETED);
        toast?.success(t('toast.success'), t('content.completed.success'));
      }
    } catch (error) {
      handleAxiosError(error as AxiosError, toast);
    }

    if (
      currentModule === modules.length - 1 &&
      currentModuleContent?.type !== CourseContentType.QUIZ
    ) {
      setTimeout(() => {
        navigate(RedirectPaths[RedirectPathsEnum.LEARNER_DASHBOARD_ACTIVE]());
      }, 500);
    } else if (
      isLastModuleCompleteble &&
      currentModule !== modules.length - 1
    ) {
      setIsCurrentModuleCompleted(false);
      setCurrentModule(currentModule + 1);
    }
  };

  const handleBackToCourse = async () => {
    const quizId = modules?.[currentModule]?.quiz?.id;
    if (!quizId) return;

    let lastAttempt: CourseAttempt | null = null;

    try {
      lastAttempt = await client.quizzes.getLastQuizAttempt(quizId);
    } catch (error: any) {
      if (error?.statusCode !== 404) {
        handleAxiosError(error as AxiosError, toast);
      }
    }

    if (
      quizId &&
      lastAttempt &&
      lastAttempt.status !== CourseStatusAttemptEnum.COMPLETED
    ) {
      try {
        await client.quizzes.abandonQuizAttempt(quizId);
      } catch (error) {
        handleAxiosError(error as AxiosError, toast);
      }
    }

    setCurrentModuleContent(null);
    setCurrentModule(0);
  };

  const isPdfReadConfirmationRequired =
    modules?.[currentModule]?.resource?.type === ResourceType.PDF &&
    !!modules?.[currentModule]?.resource?.requiresReadConfirmation;

  const continueOrFinishButton = (
    <ContinueOrFinishButton
      currentModule={currentModule}
      modules={modules}
      isCurrentModuleCompleted={isCurrentModuleCompleted}
      isContentStarted={isContentStarted}
      shouldWrapInStickyContainer={
        modules?.[currentModule]?.type !== CourseContentType.PRESENTATION &&
        modules?.[currentModule]?.resource?.type !== ResourceType.PDF
      }
      attemptLanguage={attemptLanguage || LanguagesEnum.EN}
      isDisabled={isPdfReadConfirmationRequired && !hasConfirmedRead}
      onContinue={() => {
        setCurrentModule(currentModule + 1);
        setIsCurrentModuleCompleted(false);
      }}
      onFinish={() => handleModuleCompletion(undefined, true)}
      rtl={isRTL}
    />
  );

  return (
    <>
      {(!currentUser ||
        isAttemptLoading ||
        isUserLoading ||
        !attemptLanguage) && (
        <FlexContainer direction="column" className="h-full">
          <FlexContainer direction="column" className="mt-5 flex-1">
            <ProgressSpinner />
            <h3>{t('content.loading')}</h3>
          </FlexContainer>
        </FlexContainer>
      )}

      {!isAttemptLoading &&
        currentUser &&
        course &&
        !isUserLoading &&
        attemptLanguage && (
          <StyledFlexContainer
            translate="no"
            direction="column"
            align="flex-start"
            justify="flex-start"
            className={[
              'notranslate',
              !isContentStarted && course.selfHosted ? 'stretch' : '',
            ]
              .filter(Boolean)
              .join(' ')}
            gap={24}
            height={
              modules?.[currentModule]?.type === CourseContentType.PRESENTATION
                ? '100%'
                : 'initial'
            }
          >
            {!isMobile() && (
              <ContentActionHeader
                course={course}
                status={status}
                selectedLanguage={selectedLanguage}
                setSelectedLanguage={async (language) => {
                  setSelectedLanguage(language);
                  await updateCourseAttemptLanguage(language);
                }}
                isContentStarted={isContentStarted}
                isDisabled={isSubmittingStatus}
              />
            )}

            {isMobile() && (
              <StyledButton
                className="exit-button"
                size="xs"
                icon="pi pi-times"
                isDisabled={isSubmittingStatus}
                onClick={() =>
                  navigate(
                    RedirectPaths[RedirectPathsEnum.LEARNER_DASHBOARD_ACTIVE](),
                  )
                }
              />
            )}

            {/* QUIZ */}
            {isContentStarted &&
              scormCourseAttempt &&
              modules[currentModule]?.quiz?.type === QuizType.TRADITIONAL && (
                <StyledWrapper isMobile={isMobile()}>
                  {!isMobile() && <AccessibilityIcon />}
                  <QuizComponent
                    course={course}
                    status={status}
                    quizId={modules?.[currentModule]?.quiz?.id}
                    attemptLanguage={attemptLanguage || LanguagesEnum.EN}
                    courseAttempt={scormCourseAttempt}
                    hasPreviousModules={currentModule > 0}
                    onComplete={(currentScore: number) => {
                      setScore(currentScore);
                      handleModuleCompletion(currentScore);
                    }}
                    onFinish={onQuizFinish}
                    handleBackToCourse={handleBackToCourse}
                    rtl={isRTL}
                  />
                </StyledWrapper>
              )}

            {/* VIDEO */}
            {isContentStarted &&
              modules?.[currentModule]?.type === CourseContentType.RESOURCE &&
              currentModuleContent?.resource?.type === ResourceType.VIDEO &&
              (() => {
                const videoResult = getCurrentVideoFile(
                  currentModuleContent,
                  attemptLanguage,
                );
                if (!videoResult) return null;

                return (
                  <StyledWrapper isMobile={isMobile()}>
                    <FlexContainer
                      direction="column"
                      style={{
                        width: '70%',
                        maxWidth: '1000px',
                        margin: 'auto',
                      }}
                    >
                      {videoResult.isFallback && (
                        <VideoFallbackMessage
                          severity="info"
                          text={t('course.video.fallback.playback.v2', {
                            fallbackLanguage:
                              languageDisplayLabel[
                                videoResult.fallbackLanguage ?? LanguagesEnum.EN
                              ],
                          })}
                          className="w-full mb-2"
                        />
                      )}
                      <StyledContentContainer
                        style={{ width: '100%', margin: 0 }}
                      >
                        <StyledVideo
                          playsInline
                          webkit-playsinline
                          src={videoResult.file.signedUrl}
                          controls
                          controlsList="nodownload"
                          onContextMenu={(e) => e.preventDefault()}
                          autoPlay
                          onEnded={() => {
                            setIsCurrentModuleCompleted(true);
                            if (document.fullscreenElement) {
                              document.exitFullscreen();
                            }
                          }}
                        />
                      </StyledContentContainer>
                    </FlexContainer>
                    {continueOrFinishButton}
                  </StyledWrapper>
                );
              })()}

            {/* SLIDE DECK VIEWER */}
            {/* 
            TODO: After templates are introduced we will
            add handler to dynamically switch between templates.
            Currently we are hardcoding the SlideTextImageTemplateViewer for
            all slide types.
            PresentationId will be passed for this new template switcher
            which will fetch the presentation data from the API instead of the template viewer itself.
          */}
            {isContentStarted &&
              modules?.[currentModule]?.type ===
                CourseContentType.PRESENTATION && (
                <StyledWrapper isMobile={isMobile()}>
                  <SlideTextImageTemplateViewer
                    presentationId={modules?.[currentModule]?.presentation?.id}
                    courseId={course?.id}
                    attemptLanguage={attemptLanguage}
                    hasNextModule={currentModule !== modules.length - 1}
                    hasPreviousModule={currentModule > 0}
                    onPreviousModule={() => {
                      if (currentModule > 0) {
                        setCurrentModule(currentModule - 1);
                      }
                    }}
                    continueOrFinishButton={continueOrFinishButton}
                    onLastPageReached={() => setIsCurrentModuleCompleted(true)}
                    rtl={isRTL}
                    accessibilityIcon={
                      !isMobile() ? <AccessibilityIcon /> : undefined
                    }
                  />
                </StyledWrapper>
              )}

            {/* PDF */}
            {isContentStarted &&
              modules?.[currentModule]?.type === CourseContentType.RESOURCE &&
              currentModuleContent?.resource?.type === ResourceType.PDF && (
                <StyledWrapper isMobile={isMobile()}>
                  {!isMobile() && <AccessibilityIcon />}
                  <ContentPDFViewer
                    file={
                      currentModuleContent.resource?.translations?.find(
                        (t) => t.language === attemptLanguage,
                      )?.file ?? null
                    }
                    hasPreviousModules={currentModule > 0}
                    hideNextButton={currentModule === modules.length - 1}
                    continueOrFinishButton={continueOrFinishButton}
                    requiresReadConfirmation={isPdfReadConfirmationRequired}
                    hasConfirmedRead={hasConfirmedRead}
                    attemptLanguage={attemptLanguage}
                    onReadConfirmationChange={setHasConfirmedRead}
                    onLastPageReached={() => setIsCurrentModuleCompleted(true)}
                    onPreviousModule={() => {
                      if (currentModule > 0) {
                        setCurrentModule(currentModule - 1);
                      }
                    }}
                  />
                </StyledWrapper>
              )}

            {/* FEEDBACK */}
            {isContentStarted &&
              currentModuleContent?.quiz?.type === QuizType.FEEDBACK && (
                <StyledWrapper isMobile={isMobile()}>
                  {!isMobile() && <AccessibilityIcon />}
                  <QuizFeedback
                    course={course}
                    quizFeedbackId={currentModuleContent?.quiz?.id}
                    attemptLanguage={attemptLanguage}
                    courseAttempt={scormCourseAttempt}
                    onComplete={(score) => handleModuleCompletion(score)}
                    rtl={isRTL}
                  />
                </StyledWrapper>
              )}

            {/* SCORM */}
            {(isContentStarted || !course.selfHosted) &&
              !isUrlLoading &&
              !isBellowThreshold &&
              !!modules?.length &&
              scormDetails?.url &&
              currentModuleContent?.resource?.type === ResourceType.SCORM && (
                <StyledWrapper isMobile={isMobile()}>
                  <ScormProvider
                    course={course}
                    attempt={scormCourseAttempt as CourseAttempt}
                    version={scormDetails?.version as ScormVersion}
                    user={currentUser}
                    setStatus={setStatus}
                    setScore={setScore}
                    setIsSubmittingStatus={setIsSubmittingStatus}
                    setIsBellowThreshold={setIsBellowThreshold}
                    isPreview={false}
                    isLastModule={currentModule === modules.length - 1}
                    setNextModule={() => setCurrentModule(currentModule + 1)}
                  >
                    <StyledContentContainer
                      className={isMobile() ? 'mobile' : undefined}
                    >
                      {isSubmittingStatus && (
                        <StyledSpinnerDiv>
                          <ProgressSpinner />
                          <span>{t('content.processing.results')}</span>
                        </StyledSpinnerDiv>
                      )}

                      {frameLoaded && <OrientationWarning />}

                      {isMobile() && (
                        <>
                          {status === CourseStatusAttemptEnum.COMPLETED &&
                            currentModule !== modules.length - 1 && (
                              <StyledButton
                                className="next-button"
                                size="xs"
                                icon="pi pi-chevron-right"
                                isDisabled={isSubmittingStatus}
                                onClick={() =>
                                  setCurrentModule(currentModule + 1)
                                }
                              />
                            )}
                        </>
                      )}

                      <StyledIframe
                        key={scormDetails?.url}
                        src={scormDetails?.url}
                        ref={iframeRef}
                        style={{
                          visibility: isSubmittingStatus ? 'hidden' : 'visible',
                        }}
                        onLoad={() => setFrameLoaded(true)}
                      />
                      {status === CourseStatusAttemptEnum.COMPLETED &&
                        currentModule !== modules.length - 1 &&
                        !isMobile() && (
                          <StyledNextButton
                            size="xs"
                            icon="pi pi-chevron-right"
                            isDisabled={isSubmittingStatus}
                            onClick={() => setCurrentModule(currentModule + 1)}
                          />
                        )}
                    </StyledContentContainer>
                  </ScormProvider>
                </StyledWrapper>
              )}

            {/* RETAKE SCREEN */}
            {isBellowThreshold &&
              currentModuleContent?.resource?.type === ResourceType.SCORM &&
              !!currentUser?.account?.courseMinimumScore &&
              !course.exemptFromMinimumRequirementScore && (
                <FlexContainer direction="column" gap={20} className="m-auto">
                  <h1
                    style={{
                      fontSize: headingLargeFontSize(docStyle),
                    }}
                    className="mb-5"
                  >
                    {t('content.retake.heading', {
                      percent: currentUser.account.courseMinimumScore,
                    })}
                  </h1>
                  <p className="min-score-text">{t('content.yourScore')}</p>
                  <DoughnutChart
                    id="score"
                    width="150"
                    cutout="90%"
                    isPercentCount
                    singleCount={score || 0}
                    allCount={100}
                    minScore={currentUser.account.courseMinimumScore}
                  />
                  <p
                    dangerouslySetInnerHTML={{
                      __html: t('content.retake.p', {
                        percent: `<strong>${currentUser.account.courseMinimumScore}%</strong>`,
                      }),
                    }}
                    className="min-score-text text-center mt-5"
                  />
                  <AppButton
                    label={t('content.retake')}
                    icon="pi pi-refresh"
                    iconPos="right"
                    isDisabled={isSubmittingStatus}
                    onClick={() => setIsBellowThreshold(false)}
                  />
                </FlexContainer>
              )}

            {/* LANGUAGE SELECT SCREEN */}
            {!isContentStarted &&
              (course.selfHosted || isMobile()) &&
              (!currentUser?.contentLanguage || isMobile()) && (
                <CourseLanguageSelectScreen
                  course={course}
                  initialLanguage={selectedLanguage}
                  isLoading={isUrlLoading}
                  isDisabled={!modules?.length}
                  setIsCourseStarted={async (value: boolean) => {
                    setIsContentStarted(value);
                    if (value) {
                      await attemptLog(
                        course,
                        attemptId,
                        `Course started - course: ${course.id}, selected language: ${selectedLanguage}`,
                      );
                    }
                  }}
                  setSelectedLanguage={async (language) => {
                    setSelectedLanguage(language);
                    await updateCourseAttemptLanguage(language);
                  }}
                />
              )}
          </StyledFlexContainer>
        )}
    </>
  );
};
